import { useEffect, useState, useCallback, useRef } from 'react'
import {
  LifeBuoy, Plus, Send, Filter, MessageSquare, Paperclip, X, ChevronLeft, CheckCircle2,
  ArrowLeftRight, Star, Ban, History, BarChart3, Lock, Building2, Trophy, Clock, Gauge,
} from 'lucide-react'
import api from '../api/client'
import Loader from '../components/Loader'
import { successModal, errorModal } from '../components/ui'

function load(key) { try { return JSON.parse(localStorage.getItem(key) || 'null') } catch { return null } }

const CAT_LABEL = {
  financeiro: 'Financeiro', comissoes: 'Comissões', saques: 'Saques', conexao: 'Nova Conexão',
  energia: 'Energia Compartilhada', produtos: 'Produtos', pedidos: 'Pedidos', entregas: 'Entregas',
  plataforma: 'Plataforma', cadastro: 'Cadastro', marketing: 'Marketing', treinamentos: 'Treinamentos', outros: 'Outros',
}
const STATUS_LABEL = {
  aberto: 'Aberto', em_analise: 'Em Análise', aguardando_retorno: 'Aguardando Retorno',
  em_atendimento: 'Em Atendimento', transferido: 'Transferido', resolvido: 'Resolvido',
  finalizado: 'Finalizado', cancelado: 'Cancelado',
}
const STATUS_COR = {
  aberto: 'bg-blue-100 text-blue-700', em_analise: 'bg-amber-100 text-amber-700',
  aguardando_retorno: 'bg-orange-100 text-orange-700', em_atendimento: 'bg-violet-100 text-violet-700',
  transferido: 'bg-cyan-100 text-cyan-700', resolvido: 'bg-emerald-100 text-emerald-700',
  finalizado: 'bg-gray-200 text-gray-600', cancelado: 'bg-red-100 text-red-600',
}
const DEPTO_LABEL = {
  financeiro: 'Financeiro', suporte: 'Suporte', comercial: 'Comercial',
  marketing: 'Marketing', administrativo: 'Administrativo', tecnico: 'Técnico',
}
const PRIO_COR = { baixa: 'text-gray-500', media: 'text-blue-600', alta: 'text-orange-600', urgente: 'text-red-600' }
const FECHADOS = ['resolvido', 'finalizado', 'cancelado']
const dth = (s) => (s ? new Date(s).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—')

export default function Suporte() {
  const isAdmin = (load('user')?.perfil || '') === 'admin'
  const [view, setView] = useState('tickets')   // admin: tickets | relatorios
  const [meta, setMeta] = useState({ categorias: [], prioridades: [], status: [], departamentos: [] })
  const [tickets, setTickets] = useState([])
  const [sel, setSel] = useState(null)
  const [loading, setLoading] = useState(true)
  const [novoOpen, setNovoOpen] = useState(false)
  const [filtro, setFiltro] = useState({ status: '', categoria: '', prioridade: '', departamento: '', busca: '', data_ini: '', data_fim: '' })

  useEffect(() => { api.get('/suporte/meta').then((r) => setMeta(r.data)).catch(() => {}) }, [])

  const listar = useCallback(() => {
    setLoading(true)
    const qs = new URLSearchParams()
    Object.entries(filtro).forEach(([k, v]) => v && qs.append(k, v))
    api.get('/suporte/tickets?' + qs.toString())
      .then((r) => setTickets(r.data.data || []))
      .catch(() => setTickets([])).finally(() => setLoading(false))
  }, [filtro])
  useEffect(() => { listar() }, [listar])

  const abrirTicket = (id) => api.get(`/suporte/tickets/${id}`).then((r) => setSel(r.data)).catch(() => {})

  if (sel) return <TicketDetalhe ticket={sel} isAdmin={isAdmin} meta={meta} onVoltar={() => { setSel(null); listar() }} onReload={() => abrirTicket(sel.id)} />

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-accent-400 to-emerald-500 flex items-center justify-center shadow"><LifeBuoy size={20} className="text-white" /></div>
          <div>
            <h1 className="text-2xl font-heading font-bold text-gray-900">Suporte</h1>
            <p className="text-sm text-gray-500">{isAdmin ? 'Atendimento, transferência, SLA e relatórios de tickets da rede.' : 'Abra um chamado e acompanhe seus atendimentos.'}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <div className="flex rounded-xl bg-gray-100 p-1">
              <button onClick={() => setView('tickets')} className={`px-3 py-1.5 rounded-lg text-sm font-semibold ${view === 'tickets' ? 'bg-white shadow text-gray-900' : 'text-gray-500'}`}>Tickets</button>
              <button onClick={() => setView('relatorios')} className={`px-3 py-1.5 rounded-lg text-sm font-semibold flex items-center gap-1 ${view === 'relatorios' ? 'bg-white shadow text-gray-900' : 'text-gray-500'}`}><BarChart3 size={14} /> Relatórios</button>
            </div>
          )}
          {!isAdmin && (
            <button onClick={() => setNovoOpen(true)} className="flex items-center gap-2 bg-gray-900 text-white px-4 py-2 rounded-xl text-sm font-semibold hover:bg-gray-800">
              <Plus size={16} /> Abrir Ticket
            </button>
          )}
        </div>
      </div>

      {isAdmin && view === 'relatorios' ? <Relatorios meta={meta} /> : (
        <>
          {isAdmin && (
            <div className="bg-white rounded-2xl p-3 shadow-sm border border-gray-100 flex items-center gap-2 flex-wrap">
              <Filter size={15} className="text-gray-400" />
              <select value={filtro.status} onChange={(e) => setFiltro({ ...filtro, status: e.target.value })} className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm">
                <option value="">Todos status</option>
                {meta.status.map((s) => <option key={s} value={s}>{STATUS_LABEL[s] || s}</option>)}
              </select>
              <select value={filtro.categoria} onChange={(e) => setFiltro({ ...filtro, categoria: e.target.value })} className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm">
                <option value="">Todas categorias</option>
                {meta.categorias.map((c) => <option key={c} value={c}>{CAT_LABEL[c] || c}</option>)}
              </select>
              <select value={filtro.departamento} onChange={(e) => setFiltro({ ...filtro, departamento: e.target.value })} className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm">
                <option value="">Todos deptos</option>
                {(meta.departamentos || []).map((d) => <option key={d} value={d}>{DEPTO_LABEL[d] || d}</option>)}
              </select>
              <select value={filtro.prioridade} onChange={(e) => setFiltro({ ...filtro, prioridade: e.target.value })} className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm">
                <option value="">Todas prioridades</option>
                {meta.prioridades.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
              <input type="date" value={filtro.data_ini} onChange={(e) => setFiltro({ ...filtro, data_ini: e.target.value })} className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm" title="De" />
              <input type="date" value={filtro.data_fim} onChange={(e) => setFiltro({ ...filtro, data_fim: e.target.value })} className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm" title="Até" />
              <input value={filtro.busca} onChange={(e) => setFiltro({ ...filtro, busca: e.target.value })} placeholder="Buscar nº/assunto/nome..." className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm flex-1 min-w-[160px]" />
            </div>
          )}

          {loading ? <div className="flex justify-center py-16"><Loader text="Carregando tickets..." /></div> : (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-x-auto">
              <table className="w-full text-sm whitespace-nowrap">
                <thead className="bg-gray-50 text-gray-500 text-left text-xs uppercase">
                  <tr>
                    <th className="px-3 py-2">Ticket</th><th className="px-3 py-2">Assunto</th>
                    {isAdmin && <th className="px-3 py-2">Autor</th>}
                    {isAdmin && <th className="px-3 py-2">Depto</th>}
                    <th className="px-3 py-2">Categoria</th><th className="px-3 py-2">Prioridade</th>
                    <th className="px-3 py-2">Status</th><th className="px-3 py-2">Atualizado</th><th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {tickets.map((t) => (
                    <tr key={t.id} className="border-t border-gray-100 hover:bg-primary-50/30 cursor-pointer" onClick={() => abrirTicket(t.id)}>
                      <td className="px-3 py-2 font-mono text-xs text-gray-500">{t.numero}</td>
                      <td className="px-3 py-2 font-medium text-gray-900">{t.assunto}{t.n_mensagens ? <span className="ml-2 text-[11px] text-gray-400"><MessageSquare size={11} className="inline" /> {t.n_mensagens}</span> : null}{t.avaliacao_nota ? <span className="ml-2 text-[11px] text-amber-500">{t.avaliacao_nota}★</span> : null}</td>
                      {isAdmin && <td className="px-3 py-2 text-gray-600">{t.autor_nome || '—'}</td>}
                      {isAdmin && <td className="px-3 py-2 text-gray-500 text-xs">{DEPTO_LABEL[t.departamento] || '—'}</td>}
                      <td className="px-3 py-2 text-gray-600">{CAT_LABEL[t.categoria] || t.categoria}</td>
                      <td className={`px-3 py-2 font-semibold uppercase text-[11px] ${PRIO_COR[t.prioridade]}`}>{t.prioridade}</td>
                      <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${STATUS_COR[t.status]}`}>{STATUS_LABEL[t.status] || t.status}</span></td>
                      <td className="px-3 py-2 text-gray-400 text-xs">{dth(t.updated_at)}</td>
                      <td className="px-3 py-2 text-accent-600 text-xs font-semibold">Abrir →</td>
                    </tr>
                  ))}
                  {tickets.length === 0 && <tr><td colSpan={isAdmin ? 9 : 7} className="px-4 py-10 text-center text-gray-400">Nenhum ticket {isAdmin ? '' : '— abra seu primeiro chamado'}.</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {novoOpen && <NovoTicket meta={meta} onClose={() => setNovoOpen(false)} onCriado={() => { setNovoOpen(false); listar() }} />}
    </div>
  )
}

function NovoTicket({ meta, onClose, onCriado, presets }) {
  const [f, setF] = useState({ assunto: presets?.assunto || '', categoria: presets?.categoria || 'outros', prioridade: 'media', descricao: presets?.descricao || '', pedido_numero: '', conexao_numero: '' })
  const [anexo, setAnexo] = useState(null)
  const [saving, setSaving] = useState(false)
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value })

  const fileChange = (e) => {
    const file = e.target.files?.[0]; if (!file) return
    const rd = new FileReader(); rd.onload = () => setAnexo({ nome: file.name, url: rd.result }); rd.readAsDataURL(file)
  }
  const salvar = () => {
    if (!f.assunto.trim()) return errorModal('Informe o assunto.')
    setSaving(true)
    api.post('/suporte/tickets', { ...f, anexo_url: anexo?.url, anexo_nome: anexo?.nome })
      .then(() => { successModal('Ticket aberto! Nossa equipe vai responder em breve.'); onCriado() })
      .catch(() => errorModal('Erro ao abrir ticket.')).finally(() => setSaving(false))
  }
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg p-5 space-y-3 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-heading font-bold">Abrir Ticket</h2>
          <button onClick={onClose}><X size={18} className="text-gray-400" /></button>
        </div>
        <input value={f.assunto} onChange={set('assunto')} placeholder="Assunto *" className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm" />
        <div className="grid grid-cols-2 gap-2">
          <select value={f.categoria} onChange={set('categoria')} className="border border-gray-200 rounded-xl px-3 py-2 text-sm">
            {meta.categorias.map((c) => <option key={c} value={c}>{CAT_LABEL[c] || c}</option>)}
          </select>
          <select value={f.prioridade} onChange={set('prioridade')} className="border border-gray-200 rounded-xl px-3 py-2 text-sm">
            {meta.prioridades.map((p) => <option key={p} value={p}>Prioridade: {p}</option>)}
          </select>
        </div>
        <textarea value={f.descricao} onChange={set('descricao')} rows={4} placeholder="Descreva o que precisa..." className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm" />
        <div className="grid grid-cols-2 gap-2">
          <input value={f.pedido_numero} onChange={set('pedido_numero')} placeholder="Nº do Pedido (opcional)" className="border border-gray-200 rounded-xl px-3 py-2 text-sm" />
          <input value={f.conexao_numero} onChange={set('conexao_numero')} placeholder="Nº da Conexão (opcional)" className="border border-gray-200 rounded-xl px-3 py-2 text-sm" />
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-500 cursor-pointer">
          <Paperclip size={15} /> {anexo ? anexo.nome : 'Anexar arquivo (opcional)'}
          <input type="file" className="hidden" onChange={fileChange} />
        </label>
        <button onClick={salvar} disabled={saving} className="w-full bg-gray-900 text-white py-2.5 rounded-xl text-sm font-semibold disabled:opacity-50">{saving ? 'Enviando...' : 'Abrir Ticket'}</button>
      </div>
    </div>
  )
}

function TicketDetalhe({ ticket, isAdmin, meta, onVoltar, onReload }) {
  const [msg, setMsg] = useState('')
  const [anexo, setAnexo] = useState(null)
  const [interna, setInterna] = useState(false)
  const [sending, setSending] = useState(false)
  const [transfOpen, setTransfOpen] = useState(false)
  const [aba, setAba] = useState('conversa')   // conversa | auditoria
  const [audit, setAudit] = useState([])
  const fimRef = useRef(null)
  useEffect(() => { fimRef.current?.scrollIntoView({ behavior: 'smooth' }) }, [ticket.mensagens])
  useEffect(() => {
    if (isAdmin && aba === 'auditoria') api.get(`/suporte/tickets/${ticket.id}/auditoria`).then((r) => setAudit(r.data.data || [])).catch(() => {})
  }, [aba, isAdmin, ticket.id])

  const enviar = () => {
    if (!msg.trim() && !anexo) return
    setSending(true)
    api.post(`/suporte/tickets/${ticket.id}/mensagens`, { mensagem: msg, anexo_url: anexo?.url, anexo_nome: anexo?.nome, interna: isAdmin && interna })
      .then(() => { setMsg(''); setAnexo(null); setInterna(false); onReload() })
      .catch(() => errorModal('Erro ao enviar.')).finally(() => setSending(false))
  }
  const fileChange = (e) => {
    const file = e.target.files?.[0]; if (!file) return
    const rd = new FileReader(); rd.onload = () => setAnexo({ nome: file.name, url: rd.result }); rd.readAsDataURL(file)
  }
  const mudarStatus = (status) => api.put(`/suporte/tickets/${ticket.id}`, { status }).then(onReload).catch(() => errorModal('Erro.'))
  const mudarPrio = (prioridade) => api.put(`/suporte/tickets/${ticket.id}`, { prioridade }).then(onReload).catch(() => {})
  const cancelar = () => api.post(`/suporte/tickets/${ticket.id}/cancelar`).then(onReload).catch(() => errorModal('Erro ao cancelar.'))

  const fechado = FECHADOS.includes(ticket.status)
  const podeAvaliar = !isAdmin && (ticket.status === 'resolvido' || ticket.status === 'finalizado') && !ticket.avaliacao_nota

  return (
    <div className="space-y-3">
      <button onClick={onVoltar} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800"><ChevronLeft size={16} /> Voltar</button>
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-xs text-gray-400">{ticket.numero}</span>
              <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${STATUS_COR[ticket.status]}`}>{STATUS_LABEL[ticket.status]}</span>
              <span className={`text-[11px] font-bold uppercase ${PRIO_COR[ticket.prioridade]}`}>{ticket.prioridade}</span>
              {ticket.departamento && <span className="text-[11px] text-gray-500 flex items-center gap-1"><Building2 size={11} /> {DEPTO_LABEL[ticket.departamento] || ticket.departamento}</span>}
            </div>
            <h1 className="text-xl font-heading font-bold text-gray-900 mt-1">{ticket.assunto}</h1>
            <p className="text-xs text-gray-500">{CAT_LABEL[ticket.categoria]} · por {ticket.autor_nome} · {dth(ticket.created_at)}{ticket.responsavel_nome ? ` · atend.: ${ticket.responsavel_nome}` : ''}</p>
            {ticket.avaliacao_nota ? <p className="text-xs text-amber-500 mt-1">Avaliação: {'★'.repeat(ticket.avaliacao_nota)}{'☆'.repeat(5 - ticket.avaliacao_nota)} {ticket.avaliacao_comentario ? `· "${ticket.avaliacao_comentario}"` : ''}</p> : null}
          </div>
          {isAdmin && (
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <select value={ticket.prioridade} onChange={(e) => mudarPrio(e.target.value)} className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs">
                {meta.prioridades.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
              <select value={ticket.status} onChange={(e) => mudarStatus(e.target.value)} className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs font-semibold">
                {meta.status.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
              </select>
              {!fechado && <button onClick={() => setTransfOpen(true)} className="flex items-center gap-1 border border-cyan-200 text-cyan-700 px-2.5 py-1.5 rounded-lg text-xs font-semibold hover:bg-cyan-50"><ArrowLeftRight size={13} /> Transferir</button>}
            </div>
          )}
        </div>
        {/* abas admin */}
        {isAdmin && (
          <div className="flex items-center gap-3 mt-3 border-t border-gray-100 pt-2 text-xs">
            <button onClick={() => setAba('conversa')} className={`font-semibold ${aba === 'conversa' ? 'text-accent-600' : 'text-gray-400'}`}>Conversa</button>
            <button onClick={() => setAba('auditoria')} className={`font-semibold flex items-center gap-1 ${aba === 'auditoria' ? 'text-accent-600' : 'text-gray-400'}`}><History size={12} /> Histórico/Auditoria</button>
            {!fechado && <button onClick={cancelar} className="ml-auto text-red-500 hover:text-red-700 font-semibold flex items-center gap-1"><Ban size={12} /> Cancelar</button>}
          </div>
        )}
      </div>

      {/* transferências registradas */}
      {(ticket.transferencias || []).length > 0 && isAdmin && aba === 'conversa' && (
        <div className="bg-cyan-50/60 border border-cyan-100 rounded-2xl p-3 text-xs text-cyan-900 space-y-1">
          <p className="font-bold flex items-center gap-1"><ArrowLeftRight size={12} /> Transferências</p>
          {ticket.transferencias.map((x) => (
            <p key={x.id}>{dth(x.created_at)} · {x.responsavel_anterior_nome || x.depto_origem || '—'} → {x.novo_responsavel_nome || DEPTO_LABEL[x.depto_destino] || '—'} · <span className="italic">{x.motivo}</span></p>
          ))}
        </div>
      )}

      {isAdmin && aba === 'auditoria' ? (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm divide-y divide-gray-50">
          {audit.length === 0 ? <p className="text-center text-gray-400 text-sm py-8">Sem registros de auditoria.</p> : audit.map((a) => (
            <div key={a.id} className="px-4 py-2 flex items-center justify-between text-xs">
              <span><span className="font-semibold text-gray-700">{a.acao}</span> · {a.detalhe || ''}</span>
              <span className="text-gray-400">{a.usuario_nome} · {dth(a.created_at)}</span>
            </div>
          ))}
        </div>
      ) : (
        <>
          <div className="bg-gray-50/60 rounded-2xl p-3 space-y-2 max-h-[55vh] overflow-y-auto border border-gray-100">
            {(ticket.mensagens || []).map((m) => {
              const sup = m.autor_perfil === 'admin'
              if (m.interna) return (
                <div key={m.id} className="flex justify-center">
                  <div className="max-w-[85%] rounded-xl px-3 py-2 text-xs bg-amber-50 border border-amber-200 text-amber-800">
                    <p className="font-semibold flex items-center gap-1"><Lock size={10} /> Nota interna · {m.autor_nome}</p>
                    {m.mensagem && <p className="whitespace-pre-wrap mt-0.5">{m.mensagem}</p>}
                    <p className="text-[10px] text-amber-500 mt-0.5">{dth(m.created_at)}</p>
                  </div>
                </div>
              )
              return (
                <div key={m.id} className={`flex ${sup ? 'justify-start' : 'justify-end'}`}>
                  <div className={`max-w-[78%] rounded-2xl px-3 py-2 text-sm shadow-sm ${sup ? 'bg-white border border-gray-100' : 'bg-accent-500 text-white'}`}>
                    <p className={`text-[10px] font-semibold mb-0.5 ${sup ? 'text-accent-600' : 'text-white/80'}`}>{sup ? `Suporte · ${m.autor_nome || ''}` : m.autor_nome}</p>
                    {m.mensagem && <p className="whitespace-pre-wrap">{m.mensagem}</p>}
                    {m.anexo_url && <a href={m.anexo_url} download={m.anexo_nome} className={`text-[11px] underline flex items-center gap-1 mt-1 ${sup ? 'text-accent-600' : 'text-white'}`}><Paperclip size={11} /> {m.anexo_nome || 'anexo'}</a>}
                    <p className={`text-[10px] mt-0.5 ${sup ? 'text-gray-400' : 'text-white/70'}`}>{dth(m.created_at)}</p>
                  </div>
                </div>
              )
            })}
            {(ticket.mensagens || []).length === 0 && <p className="text-center text-gray-400 text-sm py-6">Sem mensagens ainda.</p>}
            <div ref={fimRef} />
          </div>

          {podeAvaliar && <Avaliacao ticketId={ticket.id} onDone={onReload} />}

          {!fechado || ticket.status === 'resolvido' ? (
            <div className="bg-white rounded-2xl p-2 shadow-sm border border-gray-100">
              {isAdmin && (
                <label className="flex items-center gap-2 text-xs text-amber-700 px-2 pb-1 cursor-pointer">
                  <input type="checkbox" checked={interna} onChange={(e) => setInterna(e.target.checked)} /> <Lock size={11} /> Observação interna (não visível ao licenciado)
                </label>
              )}
              <div className="flex items-end gap-2">
                <label className="p-2 text-gray-400 cursor-pointer"><Paperclip size={18} /><input type="file" className="hidden" onChange={fileChange} /></label>
                <textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={1} placeholder={anexo ? `Anexo: ${anexo.nome}` : (interna ? 'Escreva uma nota interna...' : 'Escreva uma mensagem...')} className={`flex-1 resize-none border-0 outline-none text-sm py-2 max-h-28 ${interna ? 'bg-amber-50 rounded-lg px-2' : ''}`} />
                <button onClick={enviar} disabled={sending} className="bg-gray-900 text-white p-2.5 rounded-xl disabled:opacity-50"><Send size={16} /></button>
              </div>
            </div>
          ) : (
            <div className="bg-gray-100 rounded-2xl p-3 text-center text-sm text-gray-500 flex items-center justify-center gap-2"><CheckCircle2 size={16} /> Ticket {STATUS_LABEL[ticket.status].toLowerCase()}.</div>
          )}
          {!isAdmin && !fechado && <button onClick={cancelar} className="w-full text-xs text-red-500 hover:text-red-700 font-semibold flex items-center justify-center gap-1 py-1"><Ban size={12} /> Cancelar ticket</button>}
        </>
      )}

      {transfOpen && <TransferModal ticket={ticket} meta={meta} onClose={() => setTransfOpen(false)} onDone={() => { setTransfOpen(false); onReload() }} />}
    </div>
  )
}

function Avaliacao({ ticketId, onDone }) {
  const [nota, setNota] = useState(0)
  const [hover, setHover] = useState(0)
  const [comentario, setComentario] = useState('')
  const [saving, setSaving] = useState(false)
  const enviar = () => {
    if (!nota) return errorModal('Selecione de 1 a 5 estrelas.')
    setSaving(true)
    api.post(`/suporte/tickets/${ticketId}/avaliacao`, { nota, comentario })
      .then(() => { successModal('Obrigado pela avaliação!'); onDone() })
      .catch(() => errorModal('Erro ao enviar avaliação.')).finally(() => setSaving(false))
  }
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-center space-y-2">
      <p className="text-sm font-semibold text-amber-800">Como foi o atendimento?</p>
      <div className="flex items-center justify-center gap-1">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)} onClick={() => setNota(n)}>
            <Star size={28} className={(hover || nota) >= n ? 'text-amber-400 fill-amber-400' : 'text-gray-300'} />
          </button>
        ))}
      </div>
      <textarea value={comentario} onChange={(e) => setComentario(e.target.value)} rows={2} placeholder="Comentário (opcional)" className="w-full border border-amber-200 rounded-xl px-3 py-2 text-sm" />
      <button onClick={enviar} disabled={saving} className="bg-amber-500 text-white px-5 py-2 rounded-xl text-sm font-semibold disabled:opacity-50">{saving ? 'Enviando...' : 'Enviar avaliação'}</button>
    </div>
  )
}

function TransferModal({ ticket, meta, onClose, onDone }) {
  const [atendentes, setAtendentes] = useState([])
  const [novoResp, setNovoResp] = useState('')
  const [depto, setDepto] = useState(ticket.departamento || '')
  const [motivo, setMotivo] = useState('')
  const [saving, setSaving] = useState(false)
  useEffect(() => {
    api.get('/usuarios?limit=200').then((r) => setAtendentes((r.data.data || r.data || []).filter((u) => u.perfil === 'admin'))).catch(() => {})
  }, [])
  const salvar = () => {
    if (!motivo.trim()) return errorModal('Informe o motivo da transferência.')
    if (!novoResp && !depto) return errorModal('Selecione o atendente e/ou o departamento de destino.')
    setSaving(true)
    api.post(`/suporte/tickets/${ticket.id}/transferir`, { novo_responsavel_id: novoResp ? Number(novoResp) : null, depto_destino: depto || null, motivo })
      .then(() => { successModal('Ticket transferido.'); onDone() })
      .catch((e) => errorModal(e?.response?.data?.detail || 'Erro ao transferir.')).finally(() => setSaving(false))
  }
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-heading font-bold flex items-center gap-2"><ArrowLeftRight size={18} className="text-cyan-600" /> Transferir Ticket</h2>
          <button onClick={onClose}><X size={18} className="text-gray-400" /></button>
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-500">Departamento de destino</label>
          <select value={depto} onChange={(e) => setDepto(e.target.value)} className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm mt-1">
            <option value="">— manter —</option>
            {(meta.departamentos || []).map((d) => <option key={d} value={d}>{DEPTO_LABEL[d] || d}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-500">Novo responsável (atendente)</label>
          <select value={novoResp} onChange={(e) => setNovoResp(e.target.value)} className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm mt-1">
            <option value="">— sem alterar —</option>
            {atendentes.map((u) => <option key={u.id} value={u.id}>{u.nome || u.email}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-500">Motivo da transferência *</label>
          <textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={3} placeholder="Ex.: assunto financeiro, encaminhar ao setor responsável." className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm mt-1" />
        </div>
        <button onClick={salvar} disabled={saving} className="w-full bg-cyan-600 text-white py-2.5 rounded-xl text-sm font-semibold disabled:opacity-50">{saving ? 'Transferindo...' : 'Confirmar transferência'}</button>
      </div>
    </div>
  )
}

function Relatorios({ meta }) {
  const [periodo, setPeriodo] = useState({ data_ini: '', data_fim: '' })
  const [stats, setStats] = useState(null)
  const [rel, setRel] = useState(null)
  const carregar = useCallback(() => {
    api.get('/suporte/stats').then((r) => setStats(r.data)).catch(() => {})
    const qs = new URLSearchParams()
    Object.entries(periodo).forEach(([k, v]) => v && qs.append(k, v))
    api.get('/suporte/relatorios?' + qs.toString()).then((r) => setRel(r.data)).catch(() => {})
  }, [periodo])
  useEffect(() => { carregar() }, [carregar])

  const Card = ({ icon: Ic, label, value, cor }) => (
    <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
      <div className="flex items-center gap-2 text-gray-400 text-xs"><Ic size={14} className={cor} /> {label}</div>
      <p className="text-2xl font-heading font-bold text-gray-900 mt-1">{value}</p>
    </div>
  )
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card icon={LifeBuoy} label="Total de tickets" value={stats?.total ?? '—'} cor="text-accent-500" />
        <Card icon={Clock} label="Abertos" value={stats?.abertos ?? '—'} cor="text-blue-500" />
        <Card icon={CheckCircle2} label="Resolvidos" value={stats?.resolvidos ?? '—'} cor="text-emerald-500" />
        <Card icon={Gauge} label="Tempo médio 1ª resposta" value={stats?.tempo_medio_1a_resposta_h != null ? `${stats.tempo_medio_1a_resposta_h}h` : '—'} cor="text-violet-500" />
        <Card icon={Clock} label="Tempo médio de resolução" value={stats?.tempo_medio_solucao_h != null ? `${stats.tempo_medio_solucao_h}h` : '—'} cor="text-orange-500" />
        <Card icon={Gauge} label="SLA cumprido" value={stats?.sla_cumprido ?? '—'} cor="text-emerald-500" />
        <Card icon={Gauge} label="SLA vencido" value={stats?.sla_vencido ?? '—'} cor="text-red-500" />
        <Card icon={Star} label="Satisfação (NPS)" value={stats?.indice_satisfacao != null ? `${stats.indice_satisfacao}★ (${stats.avaliacoes})` : '—'} cor="text-amber-500" />
      </div>

      <div className="bg-white rounded-2xl p-3 shadow-sm border border-gray-100 flex items-center gap-2 flex-wrap">
        <Filter size={15} className="text-gray-400" />
        <span className="text-xs text-gray-500">Período do relatório:</span>
        <input type="date" value={periodo.data_ini} onChange={(e) => setPeriodo({ ...periodo, data_ini: e.target.value })} className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm" />
        <input type="date" value={periodo.data_fim} onChange={(e) => setPeriodo({ ...periodo, data_fim: e.target.value })} className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm" />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
          <h3 className="font-heading font-bold text-gray-800 mb-2 flex items-center gap-2"><Trophy size={16} className="text-amber-500" /> Ranking de Atendentes</h3>
          <div className="space-y-1 text-sm">
            {(rel?.ranking_atendentes || []).slice(0, 10).map((a, i) => (
              <div key={i} className="flex items-center justify-between border-b border-gray-50 py-1">
                <span className="text-gray-700">{i + 1}. {a.nome}</span>
                <span className="text-gray-400 text-xs">{a.total} tickets · {a.resolvidos} resolv. · {a.tempo_medio_h}h</span>
              </div>
            ))}
            {(!rel?.ranking_atendentes || rel.ranking_atendentes.length === 0) && <p className="text-gray-400 text-sm py-3">Sem dados no período.</p>}
          </div>
        </div>
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
          <h3 className="font-heading font-bold text-gray-800 mb-2 flex items-center gap-2"><Building2 size={16} className="text-cyan-500" /> Por Departamento</h3>
          <div className="space-y-1 text-sm">
            {(rel?.ranking_departamentos || []).map((d, i) => (
              <div key={i} className="flex items-center justify-between border-b border-gray-50 py-1">
                <span className="text-gray-700">{DEPTO_LABEL[d.depto] || d.depto}</span>
                <span className="text-gray-400 text-xs">{d.total} tickets</span>
              </div>
            ))}
            {(!rel?.ranking_departamentos || rel.ranking_departamentos.length === 0) && <p className="text-gray-400 text-sm py-3">Sem dados no período.</p>}
          </div>
        </div>
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
          <h3 className="font-heading font-bold text-gray-800 mb-2">Por Categoria</h3>
          <div className="space-y-1 text-sm">
            {Object.entries(rel?.por_categoria || {}).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between border-b border-gray-50 py-1"><span className="text-gray-700">{CAT_LABEL[k] || k}</span><span className="text-gray-400 text-xs">{v}</span></div>
            ))}
          </div>
        </div>
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
          <h3 className="font-heading font-bold text-gray-800 mb-2">Por Prioridade</h3>
          <div className="space-y-1 text-sm">
            {Object.entries(rel?.por_prioridade || {}).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between border-b border-gray-50 py-1"><span className={`uppercase text-xs font-semibold ${PRIO_COR[k]}`}>{k}</span><span className="text-gray-400 text-xs">{v}</span></div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
